// CSV helpers for the activity graph download. Kept separate from utils.ts so
// graph utilities (and their tests) don't pull in the download/DOM helpers.
import { V1DownloadCloudEventsActivityAccessResponse } from '@ariksa/audittrail/api';
import dayjs from 'dayjs';

import { saveAsFile } from 'utils/download';

const BASE64_PATTERN = /^[A-Za-z0-9+/]+={0,2}$/;

/* The download API returns CSV bytes, which the JSON gateway base64-encodes.
   Decode when the payload is base64 and fall back to raw text otherwise. */
export const decodeActivityAccessesCsv = (content?: string) => {
  const value = String(content ?? '');
  const compact = value.replace(/\s/g, '');
  if (!compact || compact.length % 4 !== 0 || !BASE64_PATTERN.test(compact)) {
    return value;
  }
  try {
    const binary = atob(compact);
    const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
    return new TextDecoder('utf-8').decode(bytes);
  } catch {
    return value;
  }
};

/* Build the activity graph CSV name in the same format as other Ariksa CSV
   exports: report name, resource name and local download timestamp. */
export const getActivityGraphCsvFileName = (resourceName?: string | String) =>
  'Ariksa - Activity Graph_' +
  (resourceName ? String(resourceName) + '_' : '') +
  dayjs().format('DD-MMM-YYYY h.mm A') +
  '.csv';

type ActivityCsvResponse =
  | V1DownloadCloudEventsActivityAccessResponse
  | { data?: unknown; content_type?: string; contentType?: string }
  | string
  | ArrayBuffer
  | Uint8Array
  | null
  | undefined;

/* Get the CSV text from whatever the download API sent. The generated type
   says JSON with a base64 `content`, but the endpoint can also answer with the
   CSV itself (plain text or bytes) or an HttpBody-style `data` field. Reading
   only `content` saved an empty file when the body was the CSV itself. */
export const getActivityAccessesCsvText = (response: ActivityCsvResponse) => {
  if (response == null) return '';
  // A plain-text body is the CSV itself; never base64-decode it, since a
  // one-column CSV can look like base64.
  if (typeof response === 'string') return response;
  if (response instanceof ArrayBuffer || ArrayBuffer.isView(response)) {
    return new TextDecoder('utf-8').decode(response as ArrayBuffer);
  }
  const body = response as Record<string, unknown>;
  const payload = body.content ?? body.data;
  if (payload instanceof ArrayBuffer || ArrayBuffer.isView(payload)) {
    return new TextDecoder('utf-8').decode(payload as ArrayBuffer);
  }
  return decodeActivityAccessesCsv(payload as string | undefined);
};

/* Save the activity graph CSV returned by the activity-accesses download API.
   Uses our standard file name instead of the generic API file_name. */
export const saveActivityAccessesCsv = (
  response?: ActivityCsvResponse,
  resourceName?: string | String,
) => {
  const body =
    response && typeof response === 'object' && !ArrayBuffer.isView(response)
      ? (response as Record<string, unknown>)
      : {};
  saveAsFile(
    getActivityAccessesCsvText(response),
    getActivityGraphCsvFileName(resourceName),
    String(body.content_type || body.contentType || 'text/csv;charset=utf-8;'),
  );
};
